import Modal from "../../General/ConfirmableModal";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import useAuth from "../../../hooks/useAuth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  IconButton,
  Stack,
} from "@mui/material";
import { apiVariable } from "../../../api/apiVariable";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { formatDate } from "../../../helpers/formatingFunctions";
import { camelCaseToSentenceCase } from "../../../helpers/camelCaseToSentenceCase";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import DownloadButton from "../DownloadButton/DownloadButton";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import EditIcon from "@mui/icons-material/Edit";
import * as signalR from "@microsoft/signalr";
import UpdateMaintenanceHistoryPopup from "../UpdateMaintenanceHistoryPopup/UpdateMaintenanceHistoryPopup";
import ConfirmActionButton from "../ConfirmActionButton/ConfirmActionButton";
import CompleteMaintenanceOperationButton from "../CompleteMaintenanceOperationButton/CompleteMaintenanceOperationButton";

const fetchMaintenanceOperation = async (
  token: string,
  id: string | undefined,
  assetType: string
) => {
  const url = `${apiVariable}/getAllMaintenanceOperations/${id}/${assetType}`;
  const headers = { Authorization: `Bearer ${token}` };
  const { data } = await axios.get(url, { headers });
  return data;
};

const fetchMaintenanceHistory = async (token: any, operationId: any) => {
  if (operationId !== undefined) {
    const url = `${apiVariable}/getAllMaintenanceHistory/${operationId}`;
    const headers = { Authorization: `Bearer ${token}` };
    const { data } = await axios.get(url, { headers });
    return data;
  } else {
    return [];
  }
};

const AssetMaintenanceOperations = ({
  assetType,
  operationId,
}: {
  assetType: string;
  operationId?: string;
}) => {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [expandedOperation, setExpandedOperation] = useState<string | null>(
    operationId || null
  );
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const handleSnackbarClose = () => setStatus("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [editDateOpen, setEditDateOpen] = useState<boolean>(false);
  const [newDate, setNewDate] = useState<Dayjs>(dayjs());
  const [editingOperationId, setEditingOperationId] = useState<string | null>(
    null
  );
  const [mileageEditOpen, setMileageEditOpen] = useState<boolean>(false);
  const [newMileage, setNewMileage] = useState<number>(0);
  const [loading, setLoading] = useState(false);

  const {
    data: assetMaintenanceOperations,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["assetMaintenanceOperations", id, assetType],
    queryFn: () => fetchMaintenanceOperation(token!, id, assetType),
  });

  const {
    data: assetMaintenanceOperationHistories,
    isLoading: isHistoryLoading,
    isError: isHistoryError,
    error: historyError,
  } = useQuery({
    queryKey: ["assetMaintenanceOperationHistories", expandedOperation],
    queryFn: () => fetchMaintenanceHistory(token!, expandedOperation),
    enabled: !!expandedOperation,
  });

  useEffect(() => {
    const assetMaintenanceOperationConnection =
      new signalR.HubConnectionBuilder()
        .withUrl(`${apiVariable}/maintenanceHub`, {
          accessTokenFactory: () => token || "",
          withCredentials: false,
        })
        .withAutomaticReconnect()
        .configureLogging(signalR.LogLevel.None)
        .build();

    assetMaintenanceOperationConnection.on("MaintenanceHistoryUpdated", () => {
      queryClient.invalidateQueries({
        queryKey: ["assetMaintenanceOperationHistories"],
      });
      queryClient.invalidateQueries({
        queryKey: ["assetMaintenanceOperations"],
      });
    });

    assetMaintenanceOperationConnection.start();

    return () => {
      assetMaintenanceOperationConnection.stop();
    };
  }, [token, queryClient]);

  useEffect(() => {
    if (expandedOperation) {
      queryClient
        .invalidateQueries({
          queryKey: ["assetMaintenanceOperationHistories"],
        })
        .catch((error) => {
          setStatus("error");
          setErrorMessage(
            error.response.data || "Failed to fetch maintenance histories"
          );
        });
    }
  }, [expandedOperation, queryClient]);

  const handleAccordionChange =
    (operationId: any) =>
      (event: React.SyntheticEvent, isExpanded: boolean) => {
        if (isExpanded) {
          setExpandedOperation(operationId);
        } else {
          setExpandedOperation(null);
        }
      };

  const handleEditDateOpen = (operationId: string) => {
    setEditingOperationId(operationId);
    setEditDateOpen(true);
  };

  const handleEditDateClose = () => {
    setEditDateOpen(false);
    setNewDate(dayjs());
    setEditingOperationId(null);
  };

  const handleDateChange = (date: Dayjs) => {
    setNewDate(date);
  };

  const handleDateSubmit = async () => {
    if (!newDate || !editingOperationId) {
      setStatus("error");
      setErrorMessage("Please select a valid date");
      return;
    }

    setLoading(true);
    try {
      await axios.patch(
        `${apiVariable}/editNextDueDates/${editingOperationId}`,
        newDate,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      queryClient.invalidateQueries({
        queryKey: ["assetMaintenanceOperations"],
      });
      setStatus("success");
      handleEditDateClose();
    } catch (error: any) {
      setStatus("error");
      setErrorMessage(error.response.data || "Failed to edit due date");
    } finally {
      setLoading(false);
    }
  };

  const handleEditMileageOpen = (operationId: string) => {
    setEditingOperationId(operationId);
    setMileageEditOpen(true);
  };

  const handleEditMileageClose = () => {
    setMileageEditOpen(false);
    setNewMileage(0);
    setEditingOperationId(null);
  };

  const handleMileageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewMileage(parseInt(event.target.value));
  };

  const handleMileageSubmit = async () => {
    if (!newMileage || !editingOperationId) {
      setStatus("error");
      setErrorMessage("Please select a valid mileage");
      return;
    }

    setLoading(true);
    try {
      await axios.patch(
        `${apiVariable}/editNextDueMileage/${editingOperationId}`,
        newMileage,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      queryClient.invalidateQueries({
        queryKey: ["assetMaintenanceOperations"],
      });
      setStatus("success");
      handleEditMileageClose();
    } catch (error: any) {
      setStatus("error");
      setErrorMessage(error.response.data || "Failed to edit due mileage");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, margin: "auto", mt: 4 }}>
      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Paper
          elevation={0}
          sx={{ p: 2, bgcolor: "error.light", color: "error.contrastText" }}
        >
          <Typography>Error: {(error as any).message}</Typography>
        </Paper>
      ) : (
        assetMaintenanceOperations.map((operation: any) => (
          <Accordion
            key={operation.id}
            onChange={handleAccordionChange(operation.id)}
            expanded={expandedOperation === operation.id}
            sx={{
              mb: 2,
              "&:before": {
                display: "none",
              },
              boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
              borderRadius: "8px !important",
              overflow: "hidden",
              "&.Mui-expanded": {
                margin: "16px 0",
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
              sx={{
                bgcolor: "background.paper",
                "&:hover": {
                  bgcolor: "action.hover",
                },
                "& .MuiAccordionSummary-content": {
                  margin: "12px 0",
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 500,
                  color: "text.primary",
                }}
              >
                <strong>
                  {camelCaseToSentenceCase(operation.operationPerformed)}
                </strong>{" "}
                Next Due :{" "}
                {(operation.nextDueDate && formatDate(operation.nextDueDate)) ||
                  (operation.nextDueMileage &&
                    operation.nextDueMileage + " miles") ||
                  (operation.nextDueEngineHours &&
                    operation.nextDueEngineHours + " engine hours")}{" "}
                Due In:{" "}
                {(operation.dueInDays && operation.dueInDays + " days") ||
                  (operation.dueInMileage &&
                    Math.round(operation.dueInMileage) + " miles") ||
                  (operation.dueInEngineHours &&
                    operation.dueInEngineHours + " engine hours")}
                {operation.nextDueDate && (
                  <IconButton
                    aria-label="edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditDateOpen(operation.id);
                    }}
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
                {operation.nextDueMileage && (
                  <IconButton
                    aria-label="edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditMileageOpen(operation.id);
                    }}
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {expandedOperation === operation.id && (
                <>
                  <Typography
                    sx={{ mt: 2, ml: 2, mb: 1, color: "text.secondary" }}
                  >
                    Histories:
                  </Typography>
                  <TableContainer component={Paper} elevation={0}>
                    <CompleteMaintenanceOperationButton
                      maintenanceOperationId={operation.id}
                      defaultMileage={operation?.mileage ?? null}
                      invalidateQueryKeys={[
                        ["assetMaintenanceOperations"],
                        ["assetMaintenanceOperationHistories"],
                      ]}
                      buttonLabel="Complete"
                      buttonProps={{
                        variant: "contained",
                        sx: {
                          m: 2,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 500,
                        },
                      }}
                    />
                    <Table sx={{ minWidth: 650 }}>
                      <TableBody>
                        {isHistoryLoading ? (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              sx={{ textAlign: "center", py: 3 }}
                            >
                              <CircularProgress size={24} />
                            </TableCell>
                          </TableRow>
                        ) : isHistoryError ? (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              sx={{ textAlign: "center", py: 3 }}
                            >
                              <Typography color="error">
                                Error: {historyError.message}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : assetMaintenanceOperationHistories.length > 0 ? (
                          assetMaintenanceOperationHistories.map(
                            (history: any) => (
                              <TableRow
                                key={history.id}
                                sx={{
                                  "&:hover": {
                                    bgcolor: "action.hover",
                                  },
                                }}
                              >
                                <TableCell sx={{ fontWeight: 500 }}>
                                  Date: {formatDate(history.dateFullfilled)}
                                </TableCell>
                                <TableCell>Miles: {history.mileage}</TableCell>
                                <TableCell>
                                  Engine Hours: {history.engineHours}
                                </TableCell>
                                <TableCell>Notes: {history.notes}</TableCell>
                                <TableCell>
                                  <Stack direction="row" spacing={1}>
                                    <UpdateMaintenanceHistoryPopup
                                      history={history}
                                    />
                                    <ConfirmActionButton
                                      id={history.id}
                                      entityType="MaintenanceHistory"
                                      actionType="delete"
                                      entityLabel="maintenance history"
                                      queryKey="assetMaintenanceOperationHistories"
                                      buttonLabel="Delete"
                                      buttonColor="error"
                                    />
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  <DownloadButton
                                    params={{ row: history }}
                                    type="maintenance"
                                  />
                                </TableCell>
                              </TableRow>
                            )
                          )
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              sx={{ textAlign: "center", py: 3 }}
                            >
                              <Typography color="text.secondary">
                                No history records found.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </AccordionDetails>
          </Accordion>
        ))
      )}

      <Modal
        open={editDateOpen}
        onClose={handleEditDateClose}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Paper
          sx={{
            p: 4,
            maxWidth: 400,
            width: "100%",
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            sx={{ mb: 3, fontWeight: 600 }}
          >
            Edit Next Due Date
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Next Due Date"
              value={newDate}
              onChange={(newValue) => {
                newValue && handleDateChange(newValue);
              }}
              slotProps={{
                textField: {
                  variant: "outlined",
                  fullWidth: true,
                  sx: { mb: 3 },
                },
              }}
            />
          </LocalizationProvider>
          <Button
            onClick={handleDateSubmit}
            disabled={loading}
            variant="contained"
            fullWidth
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 500,
            }}
          >
            {loading ? <CircularProgress size={24} /> : "Submit"}
          </Button>
        </Paper>
      </Modal>

      <Modal
        open={mileageEditOpen}
        onClose={handleEditMileageClose}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Paper
          sx={{
            p: 4,
            maxWidth: 400,
            width: "100%",
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            sx={{ mb: 3, fontWeight: 600 }}
          >
            Edit Next Due Mileage
          </Typography>
          <TextField
            margin="normal"
            fullWidth
            label="Next Due Mileage"
            value={newMileage}
            onChange={handleMileageChange}
            type="number"
            sx={{ mb: 3 }}
          />
          <Button
            onClick={handleMileageSubmit}
            disabled={loading}
            variant="contained"
            fullWidth
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 500,
            }}
          >
            {loading ? <CircularProgress size={24} /> : "Submit"}
          </Button>
        </Paper>
      </Modal>

      <StatusSnackbar
        status={status}
        successMessage="Maintenance operation completed successfully"
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </Box>
  );
};

export default AssetMaintenanceOperations;
